import { reminders_dismiss_title } from './DismissReminderButton.locstring.json';
import { reminders_snooze_title } from './SnoozeButton.locstring.json';
import { observer } from 'mobx-react';
import { ActionButton, IconButton } from 'office-ui-fabric-react/lib/Button';
import { ContextualMenu, IContextualMenuItem } from 'office-ui-fabric-react/lib/ContextualMenu';
import { FocusZone, FocusZoneDirection } from 'office-ui-fabric-react/lib/FocusZone';
import { Icon } from 'office-ui-fabric-react/lib/Icon';
import { PerformanceDatapoint } from 'owa-analytics';
import { ControlIcons } from 'owa-control-icons';
import { OwaDate } from 'owa-datetime';
import ReminderCharm from 'owa-header-app-notifications/lib/components/reminders/ReminderCharm';
import { ANIMATION_DURATION } from 'owa-header-app-notifications/lib/utils/reminderAnimationConstants';
import loc from 'owa-localize';
import ItemId from 'owa-service/lib/contract/ItemId';
import ReminderGroupTypes from 'owa-service/lib/contract/ReminderGroupTypes';
import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { CSSTransition } from 'react-transition-group';
import {
    InsightsDataFetchSource,
    lazyFetchMeetingInsights,
    lazyLogInsightSummaryClicked,
    lazyTryEndInsightsPerfDatapoint,
    lazyLogInsightsCacheHitMissRate,
    isInsightsInReminderEnabled,
} from 'owa-meeting-insights';
import { getReminderInsightsProps } from 'owa-meeting-insights/lib/selectors/insightsForReminderSelectors';
import {
    default as logReminderAction,
    CalendarReminderCustomData,
} from '../../utils/reminderInstrumentationHelpers';

const styles = require('./Reminder.scss');
const classnames = require('classnames/bind').bind(styles);
const animations = require('./ReminderAnimations.scss');

export interface ReminderProps {
    reminderId: ItemId;
    reminderType: ReminderGroupTypes;
    charmId: number;
    subject: string;
    location: string;
    startTimeDisplayString: string;
    timeUntilDisplayString: string;
    snoozeMenuItems: IContextualMenuItem[];
    navigateToReminder: (reminder: string, reminderType: ReminderGroupTypes) => void;
    dismissReminder: (reminder: ItemId) => void;
    now?: () => OwaDate;
}

export interface ReminderState {
    isSnoozeMenuOpen: boolean;
    isFocused: boolean;
}

@observer
export default class Reminder extends React.Component<ReminderProps, ReminderState> {
    private snoozeButton: HTMLElement;
    private reminderContainer: HTMLElement;
    private insightsPerfDatapoint: PerformanceDatapoint;

    constructor(props: ReminderProps) {
        super(props);
        this.state = {
            isSnoozeMenuOpen: false,
            isFocused: false,
        };

        const { reminderType } = this.props;
        if (isInsightsInReminderEnabled(reminderType)) {
            this.insightsPerfDatapoint = new PerformanceDatapoint(
                'RelatedContentFilesAndEmailsRenderingLatency',
                {
                    // Mark Insights datapoint as cosmosOnly when shipped to WW
                    // https://outlookweb.visualstudio.com/Outlook%20Web/_workitems/edit/58713
                    isCore: true,
                }
            );
        }
    }

    componentDidMount() {
        const { reminderId, reminderType } = this.props;

        if (isInsightsInReminderEnabled(reminderType)) {
            lazyFetchMeetingInsights
                .importAndExecute(reminderId.Id, InsightsDataFetchSource.Reminder)
                .catch(() => {
                    /*
                     * fetchMeetingInsights has to return a rejected promise to tell if the task is completed or not, for the TaskQueue to schedule the next fetch task.
                     * The catch is to swallow the thrown error from fetchMeetingInsights. No need to handle the error as it is already logged inside fetchMeetingInsights.
                     */
                });

            lazyLogInsightsCacheHitMissRate.importAndExecute(
                this.insightsPerfDatapoint,
                reminderId.Id
            );
            this.tryEndPerfDatapoint();
        }

        logReminderAction(
            'ReminderRendered',
            this.props.reminderType,
            this.getAdditionalCustomData()
        );
    }

    componentDidUpdate() {
        const { reminderType } = this.props;
        if (isInsightsInReminderEnabled(reminderType)) {
            this.tryEndPerfDatapoint();
        }
    }

    componentWillUnmount() {
        if (this.insightsPerfDatapoint) {
            // Force invalidate(end and not log) the perf datapoint if user dismisses reminder before Insights show up
            this.insightsPerfDatapoint.invalidate();
        }
        logReminderAction(
            'ReminderDismissed',
            this.props.reminderType,
            this.getAdditionalCustomData()
        );
    }

    render() {
        const {
            reminderId,
            snoozeMenuItems,
            subject,
            location,
            charmId,
            startTimeDisplayString,
            timeUntilDisplayString,
            reminderType,
        } = this.props;
        const { isSnoozeMenuOpen, isFocused } = this.state;

        // For now, insights in reminder is only enabled for Calendar type reminder
        let insightsProps;
        if (isInsightsInReminderEnabled(reminderType)) {
            insightsProps = getReminderInsightsProps(reminderId.Id);
        }
        const showThirdLine = insightsProps != null;

        /**
         * The parent <TransitionGroup> passes several properties that the CSSTransition needs.
         * However, I don't want to expose internal workings to the outside in the props,
         *  so I just pass all props down to the CSSTransition.
         **/
        return (
            <CSSTransition
                {...this.props}
                timeout={ANIMATION_DURATION}
                classNames={{
                    exit: animations.reminderLeave,
                }}>
                <FocusZone
                    className={classnames(styles.container, {
                        isSnoozeMenuOpen,
                        isFocused,
                        showThirdLine,
                    })}
                    direction={FocusZoneDirection.horizontal}
                    data-is-focusable={true}
                    ref={this.onReminderRef}>
                    <ActionButton
                        styles={{
                            root: styles.reminderButton,
                            flexContainer: styles.containerFlexContainer,
                        }}
                        data-storybook="reminder"
                        onClick={this.onReminderClick}>
                        {charmId ? (
                            <ReminderCharm iconId={charmId} className={styles.charm} />
                        ) : (
                            <Icon iconName={ControlIcons.Event} className={styles.charm} />
                        )}
                        <div className={styles.textContainer}>
                            <div className={styles.firstLine}>
                                <div className={styles.subject}>{subject}</div>
                                <div className={styles.timeUntil}>{timeUntilDisplayString}</div>
                            </div>
                            <div className={styles.secondLine}>
                                <div className={styles.startTime}>{startTimeDisplayString}</div>
                                <div className={styles.location}>{location}</div>
                            </div>
                            {showThirdLine && (
                                <div
                                    className={styles.thirdLine}
                                    onClick={this.onInsightsSummaryClicked}>
                                    <Icon
                                        iconName={insightsProps.insightsIcon}
                                        styles={{
                                            root: styles.insightsIcon,
                                        }}
                                    />
                                    <div className={styles.insightsText}>
                                        {insightsProps.insightsText}
                                    </div>
                                </div>
                            )}
                        </div>
                    </ActionButton>
                    <div className={styles.hoverActions}>
                        <IconButton
                            iconProps={{
                                iconName: ControlIcons.AlarmClock,
                            }}
                            styles={{
                                root: styles.hoverActionRoot,
                                icon: styles.hoverActionIcon,
                            }}
                            ref={this.onSnoozeButtonRef}
                            onClick={this.onToggleSnoozeMenu}
                            title={loc(reminders_snooze_title)}
                            ariaLabel={loc(reminders_snooze_title)}
                        />
                        <IconButton
                            iconProps={{
                                iconName: ControlIcons.RingerOff,
                            }}
                            styles={{
                                root: styles.hoverActionRoot,
                                icon: styles.hoverActionIcon,
                            }}
                            onClick={this.onDismissReminder}
                            title={loc(reminders_dismiss_title)}
                            ariaLabel={loc(reminders_dismiss_title)}
                        />
                    </div>
                    {this.state.isSnoozeMenuOpen && (
                        <ContextualMenu
                            calloutProps={{
                                layerProps: {
                                    className: styles.snoozeMenuLayer,
                                },
                            }}
                            items={snoozeMenuItems}
                            target={this.snoozeButton}
                            onDismiss={this.onCloseSnoozeMenu}
                        />
                    )}
                </FocusZone>
            </CSSTransition>
        );
    }

    private onSnoozeButtonRef = (ref: IconButton) => {
        this.snoozeButton = ReactDOM.findDOMNode(ref) as HTMLElement;
    };

    private onReminderClick = () => {
        !!this.props.navigateToReminder &&
            this.props.navigateToReminder(this.props.reminderId.Id, this.props.reminderType);

        logReminderAction(
            'ReminderClicked',
            this.props.reminderType,
            this.getAdditionalCustomData()
        );
    };

    private getAdditionalCustomData = (): CalendarReminderCustomData | null => {
        // For calendar reminder, return the type of insight rendered on the reminder
        if (this.props.reminderType === ReminderGroupTypes.Calendar) {
            const insightProps = getReminderInsightsProps(this.props.reminderId.Id);
            if (insightProps) {
                return { insightType: insightProps.insightType };
            }
        }

        // No insight rendered
        return null;
    };

    private onDismissReminder = (ev: React.MouseEvent<any>) => {
        ev.stopPropagation();
        !!this.props.dismissReminder && this.props.dismissReminder(this.props.reminderId);
    };

    private onToggleSnoozeMenu = (ev: React.MouseEvent<any>) => {
        ev.stopPropagation();
        this.setState({
            isSnoozeMenuOpen: !this.state.isSnoozeMenuOpen,
        });
    };

    private onCloseSnoozeMenu = (ev: React.MouseEvent<any>) => {
        ev.stopPropagation();
        this.setState({
            isSnoozeMenuOpen: false,
        });
    };

    private onReminderRef = (ref: FocusZone) => {
        // Currently, React doesn't support focusin/focusout events, despite our browser matrix doing so
        //  https://github.com/facebook/react/issues/6410
        //  https://caniuse.com/#feat=focusin-focusout-events
        // Alternatively, we can't use the :focus-within pseudo-class, since neither Edge nor IE support it,
        // and those browsers are our main accessibility stories
        //  https://caniuse.com/#feat=css-focus-within
        if (this.reminderContainer) {
            this.reminderContainer.removeEventListener('focusin', this.onFocusIn);
            this.reminderContainer.removeEventListener('focusout', this.onFocusOut);
        }

        this.reminderContainer = ReactDOM.findDOMNode(ref) as HTMLElement;

        if (this.reminderContainer) {
            this.reminderContainer.addEventListener('focusin', this.onFocusIn);
            this.reminderContainer.addEventListener('focusout', this.onFocusOut);
        }
    };

    private onFocusIn = () => {
        this.setState({
            isFocused: true,
        });
    };

    private onFocusOut = () => {
        this.setState({
            isFocused: false,
        });
    };

    private tryEndPerfDatapoint = async () => {
        const eventId = this.props.reminderId.Id;
        const hasInsightsPerfDatapointEnded = await lazyTryEndInsightsPerfDatapoint.importAndExecute(
            this.insightsPerfDatapoint,
            eventId,
            InsightsDataFetchSource.Reminder
        );
        if (hasInsightsPerfDatapointEnded) {
            // nullify the datapoint to make sure it is only logged once
            this.insightsPerfDatapoint = null;
        }
    };

    private onInsightsSummaryClicked = (evt: React.MouseEvent<HTMLElement>) => {
        lazyLogInsightSummaryClicked.importAndExecute(
            this.props.reminderId.Id /* eventId */,
            InsightsDataFetchSource.Reminder
        );
    };
}
